import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { api } from '../services/api';
import ScanUploader from './scanner/ScanUploader';
import './FundSelector.css';

// Market types
const MARKET_TYPES = {
  INDIAN_MF: 'indian_mf',
  GLOBAL_STOCKS: 'global_stocks'
};

const FundSelector = ({ selectedFunds, onFundsChange, onScanComplete, onScanError }) => {
  const [fundOptions, setFundOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('search'); // 'search' or 'scan'
  const [scanResults, setScanResults] = useState(null);
  const [marketType, setMarketType] = useState(MARKET_TYPES.INDIAN_MF);

  // Search funds when user types or market type changes
  useEffect(() => {
    const controller = new AbortController();

    const delayTimer = setTimeout(() => {
      if (searchTerm.length >= 3) {
        searchFunds(searchTerm, controller.signal);
      } else {
        setFundOptions([]);
      }
    }, 500); // Debounce search

    return () => {
      clearTimeout(delayTimer);
      controller.abort(); // Cancel any in-flight request
    };
  }, [searchTerm, marketType]);

  const searchFunds = async (query, signal) => {
    try {
      setLoading(true);

      if (marketType === MARKET_TYPES.INDIAN_MF) {
        // Search Indian mutual funds via MFAPI
        const response = await api.searchFunds(query, 20, signal);
        const options = response.data.funds.map(fund => ({
          value: fund.schemeCode,
          label: fund.schemeName,
          schemeCode: String(fund.schemeCode),
          schemeName: fund.schemeName,
          assetType: 'mutual_fund',
          source: 'mfapi'
        }));
        setFundOptions(options);
      } else {
        // Search US stocks/ETFs via Tiingo
        const response = await api.searchSecurities(query, 'stock');
        const results = response.data.results || [];
        const options = results.map(item => ({
          value: item.symbol,
          label: `${item.symbol} - ${item.name}`,
          schemeCode: item.symbol,
          schemeName: item.name,
          assetType: item.type || 'stock',
          source: 'tiingo',
          exchange: item.exchange
        }));
        setFundOptions(options);
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') return; // Aborted, ignore
      console.error('Error searching:', error);
      setFundOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFundSelection = (selectedOptions) => {
    onFundsChange(selectedOptions || []);
  };

  const handleScanUploadComplete = (results) => {
    // Call the parent handler if provided (App.js will handle auto-adding funds)
    if (onScanComplete) {
      onScanComplete(results);
    }

    // Switch back to search tab after successful scan
    setActiveTab('search');

    // Optionally store results for debugging
    setScanResults(results);
  };

  const handleScanErrorInternal = (error) => {
    console.error('Scan error:', error);

    // Call the parent handler if provided
    if (onScanError) {
      onScanError(error);
    }

    // Check if it's a "not available in simple mode" error
    if (error.message && error.message.includes('Scanner Not Available')) {
      alert(
        '📸 Scanner Feature Not Available\n\n' +
        'The OCR/scanner feature requires additional dependencies.\n\n' +
        'To use the scanner:\n' +
        '1. Install: pip install pytesseract opencv-python pdf2image pdfplumber\n' +
        '2. Install Tesseract OCR on your system\n' +
        '3. Use the full app.py instead of app_simple.py\n' +
        '4. Restart the backend\n\n' +
        'For now, please use the Search tab to manually add funds.'
      );
    } else {
      alert('Error processing scan: ' + (error.message || 'Please try again.'));
    }
  };


  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '45px',
      borderColor: '#ddd',
      '&:hover': {
        borderColor: '#aaa'
      }
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: '#e3f2fd',
      borderRadius: '4px'
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#1976d2',
      fontWeight: '500'
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#1976d2',
      '&:hover': {
        backgroundColor: '#bbdefb',
        color: '#0d47a1'
      }
    })
  };

  return (
    <div className="fund-selector">
      <div className="selector-header">
        <p className="helper-text">
          Build your portfolio with Indian mutual funds and global stocks/ETFs.
          Select at least 2 assets to proceed with optimization.
        </p>
      </div>

      {/* Market Type Selector */}
      <div className="market-type-selector">
        <button
          className={`market-type-btn ${marketType === MARKET_TYPES.INDIAN_MF ? 'active' : ''}`}
          onClick={() => { setMarketType(MARKET_TYPES.INDIAN_MF); setFundOptions([]); setSearchTerm(''); }}
        >
          🇮🇳 Indian Mutual Funds
        </button>
        <button
          className={`market-type-btn ${marketType === MARKET_TYPES.GLOBAL_STOCKS ? 'active' : ''}`}
          onClick={() => { setMarketType(MARKET_TYPES.GLOBAL_STOCKS); setFundOptions([]); setSearchTerm(''); }}
        >
          🌍 Global Stocks & ETFs
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="tab-navigation">
        <button
          className={`tab-button ${activeTab === 'search' ? 'active' : ''}`}
          onClick={() => setActiveTab('search')}
        >
          🔍 Search {marketType === MARKET_TYPES.INDIAN_MF ? 'Funds' : 'Stocks'}
        </button>
        {marketType === MARKET_TYPES.INDIAN_MF && (
          <button
            className={`tab-button ${activeTab === 'scan' ? 'active' : ''}`}
            onClick={() => setActiveTab('scan')}
          >
            📷 Scan Screenshot
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'search' && (
          <div className="search-tab">
            <Select
              isMulti
              isLoading={loading}
              options={fundOptions}
              value={selectedFunds}
              onChange={handleFundSelection}
              onInputChange={setSearchTerm}
              placeholder={marketType === MARKET_TYPES.INDIAN_MF
                ? "Type fund name to search (e.g., HDFC, ICICI)..."
                : "Type stock symbol or name (e.g., AAPL, MSFT, SPY, VOO)..."}
              noOptionsMessage={() => searchTerm.length < 2
                ? "Type at least 2 characters to search"
                : marketType === MARKET_TYPES.INDIAN_MF
                  ? "No funds found"
                  : "No stocks/ETFs found - try ticker symbols like AAPL, MSFT, SPY"}
              styles={customStyles}
              className="fund-select"
              classNamePrefix="select"
            />

            <div className="fund-recommendations">
              {marketType === MARKET_TYPES.INDIAN_MF ? (
                <p className="recommendation-note">
                  💡 Tip: Search for funds like "HDFC", "ICICI", "SBI", "Axis" to find popular fund houses
                </p>
              ) : (
                <p className="recommendation-note">
                  💡 Tip: Search stocks (AAPL, MSFT) or ETFs (SPY, VOO, QQQ) by ticker or name
                </p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'scan' && (
          <div className="scan-tab">
            <ScanUploader
              onUploadComplete={handleScanUploadComplete}
              onError={handleScanErrorInternal}
            />
          </div>
        )}
      </div>

      {/* Selected Funds Summary - Grouped by Type */}
      {selectedFunds.length > 0 && (
        <div className="selected-funds-summary">
          <h4>Selected Assets ({selectedFunds.length}):</h4>

          {/* Mutual Funds Section */}
          {selectedFunds.filter(f => f.assetType === 'mutual_fund' || !f.assetType).length > 0 && (
            <div className="asset-group">
              <h5 className="asset-group-header mf-header">
                🇮🇳 Mutual Funds ({selectedFunds.filter(f => f.assetType === 'mutual_fund' || !f.assetType).length})
              </h5>
              <div className="fund-list">
                {selectedFunds
                  .filter(f => f.assetType === 'mutual_fund' || !f.assetType)
                  .map(fund => (
                    <div key={fund.schemeCode} className="fund-item mf-item">
                      <span className="fund-code">{fund.schemeCode}</span>
                      <span className="fund-name">{fund.schemeName}</span>
                      {fund.amount && (
                        <span className="fund-amount">₹{fund.amount.toLocaleString()}</span>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Stocks/ETFs Section */}
          {selectedFunds.filter(f => f.assetType === 'stock' || f.assetType === 'etf' || f.assetType === 'Stock' || f.assetType === 'ETF').length > 0 && (
            <div className="asset-group">
              <h5 className="asset-group-header stock-header">
                🌍 Stocks & ETFs ({selectedFunds.filter(f => f.assetType === 'stock' || f.assetType === 'etf' || f.assetType === 'Stock' || f.assetType === 'ETF').length})
              </h5>
              <div className="fund-list">
                {selectedFunds
                  .filter(f => f.assetType === 'stock' || f.assetType === 'etf' || f.assetType === 'Stock' || f.assetType === 'ETF')
                  .map(fund => (
                    <div key={fund.schemeCode} className="fund-item stock-item">
                      <span className="fund-code">{fund.schemeCode}</span>
                      <span className="fund-name">{fund.schemeName}</span>
                      {fund.exchange && (
                        <span className="fund-exchange">{fund.exchange}</span>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FundSelector;